import { useState, useContext } from "react";
import dayjs from "dayjs";

import {
  Grid,
  Typography,
  Button,
  Tooltip,
  Chip,
  InputAdornment,
  Tab,
  Tabs,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  List,
  Avatar,
  Box,
  useTheme
} from "@mui/material";

import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import TaskAltIcon from "@mui/icons-material/TaskAlt";

import { TextField, ToggleButtons } from "@aclymatepackages/atoms";
import { formatDollars, formatDecimal } from "@aclymatepackages/formatters";
import { numbersRegExpTest } from "@aclymatepackages/reg-exp";
import { lbsToTons, kgsToTons } from "@aclymatepackages/converters";
import { sumTonsCo2e } from "@aclymatepackages/other-helpers";

import GrayPaper from "../../atoms/GrayPaper";
import UnitsSelect from "../../inputs/UnitsSelect";
import ListItem from "../../atoms/mui/ListItem";
import ScopeThreeCategoryAvatar from "../../atoms/icons/ScopeThreeCategoryAvatar";

import { subcategories } from "@aclymatepackages/subcategories";

import { useAccountData } from "../../../helpers/firebase";
import usePortfoliosContext from "../../../helpers/contexts/offsetsPortfolios";
import useEmissionsContext from "../../../helpers/contexts/emissions";
import { scopeThreeSpendBasedCategories } from "../../../helpers/components/scopeThreeCategories";
import { StripeCustomerContext } from "../../../helpers/contexts/stripeCustomer";
import usePurchasingContext from "../../../helpers/contexts/purchasing";

import { googlePurchaseTracking } from "../../../helpers/components/purchase";

const PurchaseByVolume = () => {
  const { selectedPortfolio } = usePortfoliosContext();
  const { displayPricePerTon } = selectedPortfolio || {};

  const { setPurchaseTons, setPurchaseDollars, setInputError } =
    usePurchasingContext();
  const [purchaseVolume, setPurchaseVolume] = useState("");
  const [volumeUnit, setVolumeUnit] = useState("tons");

  const editInputData = (inputValue, setter) => {
    setter(inputValue);
    const { volume = purchaseVolume, unit = volumeUnit } = inputValue;

    if (!numbersRegExpTest(volume)) {
      return setInputError(true);
    }

    setInputError(false);

    const purchaseTons =
      unit === "lbs"
        ? lbsToTons(volume)
        : unit === "kgs"
          ? kgsToTons(volume)
          : Number(volume);

    const purchaseDollars = purchaseTons * displayPricePerTon;
    setPurchaseDollars(purchaseDollars);
    return setPurchaseTons(purchaseTons);
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={8}>
        <TextField
          value={purchaseVolume}
          setValue={(volume) =>
            editInputData({ volume }, ({ volume }) => setPurchaseVolume(volume))
          }
          label="How many offsets?"
          error={purchaseVolume && !numbersRegExpTest(purchaseVolume)}
        />
      </Grid>
      <Grid item xs={4}>
        <UnitsSelect
          value={volumeUnit}
          setValue={(unit) =>
            editInputData({ unit }, ({ unit }) => setVolumeUnit(unit))
          }
        />
      </Grid>
    </Grid>
  );
};

const PurchaseByDollars = () => {
  const { selectedPortfolio } = usePortfoliosContext();
  const { displayPricePerTon } = selectedPortfolio || {};

  const { setPurchaseTons, setPurchaseDollars, setInputError } =
    usePurchasingContext();
  const [inputDollars, setInputDollars] = useState("");

  const editPurchaseDollars = (dollars) => {
    setInputDollars(dollars);
    if (dollars && !numbersRegExpTest(dollars)) {
      return setInputError(true);
    }

    setInputError(false);
    const tonsToPurchase = Number(dollars) / displayPricePerTon;

    setPurchaseTons(tonsToPurchase);
    return setPurchaseDollars(dollars);
  };

  return (
    <TextField
      value={inputDollars}
      setValue={editPurchaseDollars}
      label="How much do you want to pay for offsets?"
      error={!!inputDollars && !numbersRegExpTest(inputDollars)}
      InputProps={{
        startAdornment: <InputAdornment position="start">$</InputAdornment>
      }}
    />
  );
};

const ScopeSelectionChip = ({ label, onClick, isSelected, status }) => (
  <TableCell>
    <Chip
      icon={isSelected ? <TaskAltIcon /> : <RadioButtonUncheckedIcon />}
      label={formatDecimal(label)}
      onClick={onClick}
      variant={isSelected ? "filled" : "outlined"}
      disabled={status === "disabled"}
    />
  </TableCell>
);

const ScopeYearSelectionRow = ({
  year,
  isPlatinum,
  diamondTons,
  platinumTons,
  onSelectChip,
  selectedChips,
  accountingYears,
  badge
}) => {
  const findChipsStatus = ({ type: currentYearType }) => {
    const findPlatinumChipsStatus = () => {
      if (
        isPlatinum ||
        badge === "platinum" ||
        badge === "diamond" ||
        !platinumTons
      ) {
        return "disabled";
      }

      const earlierYears = accountingYears.filter(
        (row) => row.year < year && !row.isPlatinum
      );

      if (!earlierYears.length) {
        return "selectable";
      }

      const areEarlierYearsChecked = earlierYears.reduce(
        (acc, { year }) =>
          !!selectedChips.find((chip) => chip.year === year) && acc,
        true
      );

      if (areEarlierYearsChecked) {
        return "selectable";
      }

      return "disabled";
    };

    const findDiamondChipsStatus = () => {
      const isYearSelected = !!selectedChips.find((chip) => chip.year === year);
      if (!isYearSelected && !isPlatinum && !!platinumTons) {
        return "disabled";
      }

      return "selectable";
    };

    if (currentYearType === "platinum") {
      return findPlatinumChipsStatus();
    }

    return findDiamondChipsStatus();
  };

  const chipsArray = [
    {
      label: platinumTons,
      value: platinumTons,
      type: "platinum",
      alreadyPurchased:
        isPlatinum || badge === "platinum" || badge === "diamond"
    },
    {
      label: diamondTons,
      value: diamondTons,
      type: "diamond",
      alreadyPurchased: badge === "diamond"
    }
  ];

  const chipsWithStatuses = chipsArray.map((chip) => ({
    status: findChipsStatus(chip),
    ...chip
  }));

  return (
    <TableRow>
      <TableCell>
        <Typography variant="h4">{year}</Typography>
      </TableCell>
      {chipsWithStatuses.map(
        ({ label, type, value, status, alreadyPurchased }, idx) => (
          <ScopeSelectionChip
            key={`scope-selection-chip-${idx}`}
            status={status}
            label={label}
            onClick={() => onSelectChip(type, value)}
            isSelected={
              alreadyPurchased ||
              !!selectedChips.find(
                (chip) => chip?.year === year && chip?.type === type
              )
            }
          />
        )
      )}
    </TableRow>
  );
};

const ScopeBadgeHeaderCell = ({ badge, label }) => (
  <Grid container direction="column" spacing={2} alignItems="center">
    <Grid item>
      <img
        src={`/images/badges/svg/${badge}.svg`}
        style={{ width: "125px" }}
        alt={badge}
      />
    </Grid>
    <Grid>
      <Typography align="center" variant="body1">
        {label}
      </Typography>
    </Grid>
  </Grid>
);

const PurchaseByScope = () => {
  const { selectedPortfolio } = usePortfoliosContext();
  const { displayPricePerTon } = selectedPortfolio || {};
  const {
    setSelectedScopeChips,
    setPurchaseTons,
    setPurchaseDollars,
    nonDiamondYears
  } = usePurchasingContext();

  const [selectedChips, setSelectedChips] = useState([]);

  const onSelectChip = (year) => (type, tonsCo2e) =>
    setSelectedChips((currentChips) => {
      const findMatchingChip = (chip) =>
        chip?.year === year && chip?.type === type;

      const handleNewChips = (newChips) => {
        setSelectedScopeChips(newChips);
        const purchaseTons = sumTonsCo2e(newChips);
        const purchaseDollars = purchaseTons * displayPricePerTon;

        setPurchaseTons(purchaseTons);
        return setPurchaseDollars(purchaseDollars);
      };

      const existingChip = currentChips.find(findMatchingChip);

      if (existingChip) {
        const newChips = currentChips.filter((chip) => !findMatchingChip(chip));
        handleNewChips(newChips);
        return newChips;
      }

      const newChips = [...currentChips, { year, type, tonsCo2e }];
      handleNewChips(newChips);
      return newChips;
    });

  const headersArray = [
    <Typography variant="body1" align="center">
      Year
    </Typography>,
    <ScopeBadgeHeaderCell badge="platinum-offsets" label="Scope 1+2 tons" />,
    <ScopeBadgeHeaderCell badge="diamond-offsets" label="Scope 3 tons" />
  ];

  return (
    <Table>
      <TableContainer>
        <TableHead>
          <TableRow style={{ verticalAlign: "bottom" }}>
            {headersArray.map((header, idx) => (
              <TableCell key={`table-head-cell-${idx}`}>{header}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {nonDiamondYears.map(({ year, ...otherRowProps }, idx) => (
            <ScopeYearSelectionRow
              key={`scope-year-selection-row-${idx}`}
              selectedChips={selectedChips}
              onSelectChip={onSelectChip(year)}
              year={year}
              accountingYears={nonDiamondYears}
              {...otherRowProps}
            />
          ))}
        </TableBody>
      </TableContainer>
    </Table>
  );
};

const EventListItem = ({ event, onSelectEvent, selectedEvents }) => {
  const { id: eventId, name, tonsCo2e } = event;

  const isEventSelected = selectedEvents.find(({ id }) => id === eventId);

  return (
    <ListItem
      primaryText={name}
      secondaryText={`${formatDecimal(tonsCo2e)} Metric Tons`}
      actions={[
        {
          tooltip: "Select this event to offset",
          onClick: () => onSelectEvent(event),
          icon: isEventSelected ? <TaskAltIcon /> : <RadioButtonUncheckedIcon />
        }
      ]}
    />
  );
};

const PurchaseByEvents = () => {
  const { selectedPortfolio } = usePortfoliosContext();
  const { displayPricePerTon } = selectedPortfolio || {};

  const {
    setPurchaseTons,
    setPurchaseDollars,
    eventsThatCanBeOffset,
    selectedEvents,
    setSelectedEvents
  } = usePurchasingContext();

  const onSelectEvent = (newSelectedEvent) =>
    setSelectedEvents((currentSelectedEvents) => {
      const { id: newlySelectedEventId } = newSelectedEvent;
      const isEventAlreadySelected = currentSelectedEvents.find(
        ({ id }) => id === newlySelectedEventId
      );

      const handleNewSelectedEvents = (newSelectedEvents) => {
        const purchaseTons = sumTonsCo2e(newSelectedEvents);
        const purchaseDollars = purchaseTons * displayPricePerTon;

        setPurchaseTons(purchaseTons);
        return setPurchaseDollars(purchaseDollars);
      };

      if (isEventAlreadySelected) {
        const filteredSelectedEvents = currentSelectedEvents.filter(
          ({ id }) => id !== newlySelectedEventId
        );

        handleNewSelectedEvents(filteredSelectedEvents);
        return filteredSelectedEvents;
      }

      const newSelectedEvents = [...currentSelectedEvents, newSelectedEvent];
      handleNewSelectedEvents(newSelectedEvents);
      return newSelectedEvents;
    });

  return (
    <List sx={{ maxHeight: "25rem", overflowY: "auto" }}>
      {eventsThatCanBeOffset.map((event, idx) => (
        <EventListItem
          key={`event-row-${idx}`}
          event={event}
          onSelectEvent={onSelectEvent}
          selectedEvents={selectedEvents}
        />
      ))}
    </List>
  );
};

const ScopeNumberBadge = ({ scope }) => {
  const { palette } = useTheme();
  const badgeColors = {
    1: palette.primary.main,
    2: palette.secondary.main,
    3: palette.backgroundGray.dark
  };
  return (
    <Avatar style={{ backgroundColor: badgeColors[scope], height: "30px", width: "30px" }}>
      {`S${scope}`}
    </Avatar>
  );
};

const ScopeCategoryRow = ({ icon, label, tonsCo2e, isSelected, onSelect, indented }) => (
  <Box sx={indented ? { pl: 4 } : {}}>
    <ListItem
      icon={icon}
      primaryText={label}
      secondaryText={tonsCo2e ? `${formatDecimal(tonsCo2e)} Metric Tons` : "No data for this year"}
      actions={[
        {
          tooltip: isSelected ? "Deselect" : "Select to offset",
          onClick: onSelect,
          icon: isSelected ? <TaskAltIcon /> : <RadioButtonUncheckedIcon />,
          disabled: !tonsCo2e
        }
      ]}
    />
  </Box>
);

const PurchaseByScopeCategories = () => {
  const { selectedPortfolio } = usePortfoliosContext();
  const { displayPricePerTon } = selectedPortfolio || {};

  const { setPurchaseTons, setPurchaseDollars } = usePurchasingContext();
  const { allEmissions = [] } = useEmissionsContext();
  const [{ startDate }] = useAccountData();

  const numberOfYears = dayjs().year() - dayjs(startDate).year() + 1;
  const yearsArray = [...new Array(numberOfYears)].map((_, idx) => {
    const year = dayjs(startDate).year() + idx;
    return { value: year, name: year };
  });

  const scopeEmissionsByYear = yearsArray.map(({ value: year }) => {
    const yearEmissions = allEmissions.filter(({ date, emissionDate }) =>
      dayjs(emissionDate || date).year() === year
    );

    const scope3Emissions = yearEmissions.filter(({ scope }) => scope === 3);
    const scope3Categories = scopeThreeSpendBasedCategories
      .map(({ subcategory, scopeThreeCategory }) => ({
        subcategory,
        scopeThreeCategory,
        tonsCo2e: sumTonsCo2e(
          scope3Emissions.filter(
            ({ subcategory: emSub }) =>
              (subcategories.find((s) => s.subcategory === emSub) || {})
                .scopeThreeCategory === scopeThreeCategory
          )
        )
      }));

    return {
      year,
      scope1Tons: sumTonsCo2e(yearEmissions.filter(({ scope }) => scope === 1)),
      scope2Tons: sumTonsCo2e(yearEmissions.filter(({ scope }) => scope === 2)),
      scope3Categories
    };
  });

  const [displayYear, setDisplayYear] = useState(
    yearsArray[yearsArray.length - 1]?.value || dayjs().year()
  );
  const [selectedItems, setSelectedItems] = useState([]);

  const currentYearData = scopeEmissionsByYear.find(({ year }) => year === displayYear) || {
    scope1Tons: 0,
    scope2Tons: 0,
    scope3Categories: []
  };

  const isItemSelected = (year, scope) =>
    !!selectedItems.find((item) => item.year === year && item.scope === scope);

  const updatePurchaseTotals = (newItems) => {
    const totalTons = newItems.reduce((sum, { tonsCo2e }) => sum + tonsCo2e, 0);
    setPurchaseTons(totalTons);
    setPurchaseDollars(totalTons * displayPricePerTon);
  };

  const handleToggleItem = (year, scope, tonsCo2e) =>
    setSelectedItems((current) => {
      const isAlreadySelected = current.find(
        (item) => item.year === year && item.scope === scope
      );
      const newItems = isAlreadySelected
        ? current.filter((item) => !(item.year === year && item.scope === scope))
        : [...current, { year, scope, tonsCo2e }];
      updatePurchaseTotals(newItems);
      return newItems;
    });

  const scope3CategoriesWithData = currentYearData.scope3Categories.filter(
    ({ tonsCo2e }) => tonsCo2e > 0
  );
  const allScope3Selected =
    scope3CategoriesWithData.length > 0 &&
    scope3CategoriesWithData.every(({ subcategory }) =>
      isItemSelected(displayYear, subcategory)
    );

  const handleToggleAllScope3 = () =>
    setSelectedItems((current) => {
      const withoutThisYearScope3 = current.filter(
        (item) =>
          !(
            item.year === displayYear &&
            item.scope !== "scope1" &&
            item.scope !== "scope2"
          )
      );

      if (allScope3Selected) {
        updatePurchaseTotals(withoutThisYearScope3);
        return withoutThisYearScope3;
      }

      const allScope3Items = scope3CategoriesWithData
        .map(({ subcategory, tonsCo2e }) => ({ year: displayYear, scope: subcategory, tonsCo2e }));
      const newItems = [...withoutThisYearScope3, ...allScope3Items];
      updatePurchaseTotals(newItems);
      return newItems;
    });

  const selectableScope3Categories = scopeThreeSpendBasedCategories
    .map((cat) => {
      const yearData = currentYearData.scope3Categories.find(
        ({ subcategory }) => subcategory === cat.subcategory
      );
      return { ...cat, tonsCo2e: yearData?.tonsCo2e || 0 };
    })
    .filter(({ tonsCo2e }) => tonsCo2e > 0);

  const totalScope3Tons = sumTonsCo2e(currentYearData.scope3Categories);

  return (
    <Grid container spacing={2} direction="column">
      {yearsArray.length > 1 && (
        <Grid item>
          <Typography variant="body2" color="textSecondary">
            Attribution Year:
          </Typography>
          <ToggleButtons
            value={displayYear}
            buttons={yearsArray}
            onChange={setDisplayYear}
          />
        </Grid>
      )}
      <Grid item>
        <List disablePadding>
          <ScopeCategoryRow
            icon={<ScopeNumberBadge scope={1} />}
            label="Scope 1"
            tonsCo2e={currentYearData.scope1Tons}
            isSelected={isItemSelected(displayYear, "scope1")}
            onSelect={() =>
              handleToggleItem(displayYear, "scope1", currentYearData.scope1Tons)
            }
          />
          <ScopeCategoryRow
            icon={<ScopeNumberBadge scope={2} />}
            label="Scope 2"
            tonsCo2e={currentYearData.scope2Tons}
            isSelected={isItemSelected(displayYear, "scope2")}
            onSelect={() =>
              handleToggleItem(displayYear, "scope2", currentYearData.scope2Tons)
            }
          />
          <ScopeCategoryRow
            icon={<ScopeNumberBadge scope={3} />}
            label="All Scope 3"
            tonsCo2e={totalScope3Tons}
            isSelected={allScope3Selected}
            onSelect={handleToggleAllScope3}
          />
          {selectableScope3Categories.map(({ subcategory, name, tonsCo2e, scopeThreeCategory }) => (
            <ScopeCategoryRow
              key={subcategory}
              icon={
                <ScopeThreeCategoryAvatar
                  scopeThreeCategory={scopeThreeCategory}
                  size="small"
                />
              }
              label={name}
              tonsCo2e={tonsCo2e}
              isSelected={isItemSelected(displayYear, subcategory)}
              onSelect={() => handleToggleItem(displayYear, subcategory, tonsCo2e)}
              indented
            />
          ))}
          <Box sx={{ px: 2, pt: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Totals reflect only transactions assigned to a Scope 3 category.
              To offset your complete Scope 3 footprint, ensure all vendors are
              categorized before purchasing.
            </Typography>
          </Box>
        </List>
      </Grid>
    </Grid>
  );
};

const VolumeSelectionTabs = () => {
  const {
    setPurchaseTons,
    setPurchaseDollars,
    selectedTab,
    setSelectedTab,
    nonDiamondYears,
    eventsThatCanBeOffset
  } = usePurchasingContext();

  const tabsContent = {
    scopes: <PurchaseByScopeCategories />,
    certifications: <PurchaseByScope />,
    volume: <PurchaseByVolume />,
    dollars: <PurchaseByDollars />,
    events: <PurchaseByEvents />
  };

  const certificationTabObj = nonDiamondYears.length
    ? [{ label: "Certifications", value: "certifications" }]
    : [];
  const eventsTabObj = eventsThatCanBeOffset.length
    ? [{ label: "Events", value: "events" }]
    : [];

  return (
    <>
      <Grid item container justifyContent="center">
        <Grid item>
          <Tabs
            value={selectedTab}
            onChange={(_, value) => {
              setPurchaseTons(0);
              setPurchaseDollars(0);
              setSelectedTab(value);
            }}
            variant="full-width"
            indicatorColor="secondary"
          >
            {[
              { label: "Volume", value: "volume" },
              { label: "Scopes", value: "scopes" },
              { label: "Dollars", value: "dollars" },
              ...certificationTabObj,
              ...eventsTabObj
            ].map((tab, idx) => (
              <Tab key={`tab-${idx}`} {...tab} />
            ))}
          </Tabs>
        </Grid>
      </Grid>
      <Grid item>{tabsContent[selectedTab]}</Grid>
    </>
  );
};

const FooterAnnotation = ({ purchaseTons, purchaseDollars }) => {
  const { selectedPortfolio } = usePortfoliosContext();
  const { displayPricePerTon } = selectedPortfolio || {};

  const DisplayNumber = ({ number, caption }) => (
    <Grid item>
      <Typography variant="h2" align="center">
        {number}
      </Typography>
      <Typography
        display="block"
        align="center"
        variant="caption"
        color="textSecondary"
      >
        {caption}
      </Typography>
    </Grid>
  );

  if (purchaseTons) {
    return (
      <Grid
        item
        container
        spacing={2}
        alignItems="center"
        justifyContent="center"
      >
        <DisplayNumber number={`$${displayPricePerTon}`} caption="per tonne" />
        <Grid item>
          <Typography variant="h1">*</Typography>
        </Grid>
        <DisplayNumber number={formatDecimal(purchaseTons)} caption="tonnes" />
        <Grid item>
          <Typography variant="h1">=</Typography>
        </Grid>
        <DisplayNumber
          number={formatDollars(purchaseDollars)}
          caption="Total Cost"
        />
      </Grid>
    );
  }

  return <></>;
};

const Footer = ({ showNextStep, setShowNextStep, viewMode = "company" }) => {
  const { selectedPortfolio } = usePortfoliosContext();
  const {
    inputError,
    purchaseDollars,
    purchaseTons,
    selectedTab,
    attributionYear,
    setAttributionYear,
    diamondYears,
    dataLoading
  } = usePurchasingContext();

  const [{ startDate = dayjs.year() }] = useAccountData();
  const { paymentMethod } = useContext(StripeCustomerContext);
  const { paymentMethodId } = paymentMethod || {};

  const isNotCompanyViewMode = viewMode !== "company";

  const numberOfAttributionYears = dayjs().year() - dayjs(startDate).year() + 1;

  const yearsArray = [...new Array(numberOfAttributionYears)].map((_, idx) => {
    const year = dayjs(startDate).year() + idx;
    return { value: year, name: year };
  });
  const filteredYearsArray = yearsArray.filter(
    ({ value }) => !diamondYears.find(({ year }) => year === value)
  );

  const isNextStepDisabled =
    !purchaseTons || inputError || isNotCompanyViewMode || dataLoading;

  const purchaseTrackingObj = {
    portfolio: selectedPortfolio,
    purchaseDollars,
    purchaseTons
  };

  return (
    <>
      <FooterAnnotation
        purchaseTons={purchaseTons}
        purchaseDollars={purchaseDollars}
      />
      <Grid
        item
        container
        justifyContent={
          isNotCompanyViewMode ||
          selectedTab === "certifications" ||
          selectedTab === "scopes"
            ? "center"
            : "space-between"
        }
        spacing={2}
        alignItems="flex-end"
      >
        {!isNotCompanyViewMode &&
          selectedTab !== "certifications" &&
          selectedTab !== "scopes" && (
          <Grid item>
            <Typography variant="body2">Attribution Year:</Typography>
            <ToggleButtons
              value={attributionYear}
              buttons={filteredYearsArray}
              onChange={setAttributionYear}
            />
          </Grid>
        )}
        {!showNextStep && (
          <Grid item>
            <Tooltip
              title="You can browse offsets in non-company view mode but not purchase them"
              disableHoverListener={!isNotCompanyViewMode}
            >
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={isNextStepDisabled}
                  onClick={() => {
                    googlePurchaseTracking("add_to_cart", purchaseTrackingObj);
                    googlePurchaseTracking(
                      "begin_checkout",
                      purchaseTrackingObj
                    );
                    return setShowNextStep(true);
                  }}
                >
                  {!!paymentMethodId ? "Review Purchase" : "Setup billing"}
                </Button>
              </span>
            </Tooltip>
          </Grid>
        )}
      </Grid>
    </>
  );
};

const PurchaseVolumeSelectionInput = ({
  showNextStep,
  setShowNextStep,
  viewMode = "company"
}) => {
  return (
    <GrayPaper>
      <Grid container spacing={2} direction="column">
        <Grid item>
          <Typography variant="h3">Select how many credits you need</Typography>
        </Grid>
        <VolumeSelectionTabs />
        <Footer
          showNextStep={showNextStep}
          setShowNextStep={setShowNextStep}
          viewMode={viewMode}
        />
      </Grid>
    </GrayPaper>
  );
};
export default PurchaseVolumeSelectionInput;
